// @ts-nocheck
/* eslint-disable */
import React, { useMemo, useCallback, useEffect } from 'react';
import useAppStore from '../store/index'; 
import { currencyLabel } from '../utils/currencyFormatter';
import { resolveEffectiveClientMwstSatz } from '../store/slices/settingsSlice';

/**
 * --- INVESTRECHNER PAGE (MONOLITH V2.9 - CARBON EDITION) ---
 * Status: UI-Reset aktiv | Texte lektoriert | Stepper für Top-Inputs hinzugefügt | Globale Rentabilität angebunden
 */

// --- HILFSFUNKTIONEN ---
const parseNumber = (text: any) => {
    if (text === null || text === undefined) return 0;
    if (typeof text === 'number') return text;
    let s = String(text).trim();
    if (s.includes(',') && s.includes('.')) {
        if (s.lastIndexOf('.') < s.lastIndexOf(',')) { s = s.replace(/\./g, '').replace(',', '.'); }
        else { s = s.replace(/,/g, ''); }
    } else if (s.includes(',')) { s = s.replace(',', '.'); }
    const cleaned = s.replace(/[^0-9.-]/g, '');
    const number = parseFloat(cleaned);
    return isNaN(number) ? 0 : number;
};

const formatNumberDisplay = (num: number, minDecimals = 0, maxDecimals = 0) => {
    const val = isNaN(num) ? 0 : num;
    return new Intl.NumberFormat('de-DE', { 
        minimumFractionDigits: minDecimals, 
        maximumFractionDigits: maxDecimals 
    }).format(val);
};

// --- SUB-KOMPONENTEN ---

const EditableField = ({ value, onSave, format = 'integer', className = '', disabled = false, unit = '', tooltip }: any) => {
    const [localValue, setLocalValue] = React.useState('');
    const [isEditing, setIsEditing] = React.useState(false);
    
    const getDisplayValue = useCallback((val: any) => {
        const numVal = parseNumber(val);
        if (format === 'integer') return formatNumberDisplay(numVal, 0, 0); 
        if (format === 'currency') return formatNumberDisplay(numVal, 2, 2);
        if (format === 'decimal1') return formatNumberDisplay(numVal, 1, 1);
        return String(val);
    }, [format]);

    useEffect(() => {
        if (!isEditing) setLocalValue(getDisplayValue(value));
    }, [value, isEditing, getDisplayValue]);

    const handleBlur = () => {
        if (disabled) return;
        setIsEditing(false);
        const numVal = parseNumber(localValue);
        onSave(numVal);
    };

    const handleFocus = (e: any) => {
        if (!disabled) {
            setIsEditing(true);
            setTimeout(() => e.target.select(), 10);
        }
    };

    return (
        <div 
            className={`noble-input-wrapper ${className} ${disabled ? 'disabled' : ''} ${tooltip ? 'cursor-help' : ''}`}
            data-tooltip-id={tooltip ? "app-tooltip" : undefined} 
            data-tooltip-content={tooltip}
        >
            <input 
                type="text" 
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={(e: any) => { if (e.key === 'Enter') e.target.blur(); }}
                disabled={disabled}
            />
            {unit && <span className="noble-suffix">{unit}</span>}
        </div>
    );
};

// 🔴 CHIRURGIE-EINHEIT: Neuer Wrapper für die +/- Buttons (Stepper)
/** minDigits: sichtbare Ziffernbreite (Investition 4 → „2.800“, Rate 3 → „900“) */
const StepperField = ({
    value,
    onSave,
    step,
    format = 'integer',
    className = '',
    disabled = false,
    unit = '',
    tooltip,
    minDigits = 3,
}: any) => {
    const handleStep = (direction: number) => {
        if (disabled) return;
        const current = parseNumber(value);
        let next = current + (direction * step);
        if (next < 0) next = 0; 
        onSave(next);
    };
    // Ziffern + Tausenderpunkt + Einheit (EUR/Mon) — verhindert Abschneiden wie bei „2.800 EUR“
    const inputMinWidth =
        minDigits >= 4 ? 'calc(5.5ch + 2.6rem)' : minDigits >= 3 ? 'calc(4ch + 2.4rem)' : 'calc(3ch + 2.2rem)';

    return (
            <div className="flex items-center gap-1 w-full max-w-full min-w-0 box-border justify-center">
            <button 
                type="button"
                onClick={() => handleStep(-1)} 
                disabled={disabled} 
                className="stepper-btn shadow-lg shrink-0"
                title={`-${step}`}
            >−</button>
            <div
                className="flex-1 shadow-inner rounded-md min-w-0"
                style={{ minWidth: inputMinWidth, maxWidth: '100%' }}
            >
                <EditableField 
                    value={value} 
                    onSave={onSave} 
                    format={format} 
                    className={className} 
                    disabled={disabled} 
                    unit={unit} 
                    tooltip={tooltip} 
                />
            </div>
            <button 
                type="button"
                onClick={() => handleStep(1)} 
                disabled={disabled} 
                className="stepper-btn shadow-lg shrink-0"
                title={`+${step}`}
            >+</button>
        </div>
    );
};

const CalculatedField = ({ value, format = 'integer', className = '', unit = '', tooltip }: any) => {
    const numVal = parseNumber(value);
    const formattedValue = format === 'decimal' ? formatNumberDisplay(numVal, 2, 2) :
                           format === 'decimal1' ? formatNumberDisplay(numVal, 1, 1) : 
                           format === 'currency' ? formatNumberDisplay(numVal, 2, 2) : 
                           formatNumberDisplay(numVal, 0, 0);

    return ( 
        <div 
            className={`noble-result-wrapper ${className} ${tooltip ? 'cursor-help' : ''}`}
            data-tooltip-id={tooltip ? "app-tooltip" : undefined} 
            data-tooltip-content={tooltip}
        >
            <input type="text" readOnly value={formattedValue} className="text-customBeige font-mono font-bold drop-shadow-md" />
            {unit && <span className="noble-suffix">{unit}</span>}
        </div>
    );
};

const ResultBox = ({ label, value, unit, format = 'integer', modeActive, tooltip }: any) => (
    <div 
        className={`result-kachel-elite shadow-[inset_0_2px_15px_rgba(0,0,0,0.5)] ${tooltip ? 'cursor-help' : ''}`}
        data-tooltip-id={tooltip ? "app-tooltip" : undefined} 
        data-tooltip-content={tooltip}
    >
        <label className="invest-result-label pointer-events-none uppercase tracking-widest text-[10px] text-stone-500 dark:text-gray-500 mb-2 font-bold">{label}</label>
        <div className="result-content pointer-events-none">
            {modeActive ? (
                <>
                    <span className="value text-pe-accent-text dark:text-[#E6C299] drop-shadow-md font-black font-mono">{format === 'decimal' ? formatNumberDisplay(value, 2, 2) : formatNumberDisplay(value, 0, 0)}</span>
                    <span className="unit text-xs uppercase tracking-widest text-stone-500 dark:text-gray-600">{unit}</span>
                </>
            ) : (
                <span className="value text-[#222]">---</span>
            )}
        </div>
    </div>
);

const CostTile = ({ title, netto, brutto, tooltip }: any) => (
    <div 
        className={`cost-tile-clean shadow-[inset_0_2px_15px_rgba(0,0,0,0.5)] ${tooltip ? 'cursor-help' : ''}`}
        data-tooltip-id={tooltip ? "app-tooltip" : undefined} 
        data-tooltip-content={tooltip}
    >
        <h4 className="cost-tile-label pointer-events-none uppercase tracking-widest text-xs font-bold text-pe-muted dark:text-gray-400 mb-4">{title}</h4>
        <div className="v-row pointer-events-none border-b border-pe-border dark:border-[#222] pb-2 w-full justify-between"><span className="unt text-stone-500 dark:text-gray-600 uppercase tracking-widest">netto</span><span className="val text-pe-accent-text dark:text-[#E6C299]">{formatNumberDisplay(netto, 0, 0)}</span></div>
        <div className="v-row pointer-events-none pt-2 w-full justify-between"><span className="unt text-stone-500 dark:text-gray-600 uppercase tracking-widest">brutto</span><span className="val text-pe-text dark:text-white">{formatNumberDisplay(brutto, 0, 0)}</span></div>
    </div>
);

const CollapseSection = ({ title, isExpanded, setIsExpanded, children }: any) => {
     return (
         <div className="mb-6 bg-pe-card dark:bg-[#0a0a0a] rounded-xl border border-pe-border dark:border-[#222] shadow-xl overflow-hidden transition-all">
             <div onClick={() => setIsExpanded(!isExpanded)} className="flex justify-between items-center cursor-pointer hover:bg-pe-hover dark:hover:bg-white/5 p-4 transition-colors">
                 <h3 className="text-sm font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]">{title}</h3>
                 <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 text-stone-500 dark:text-gray-500 transition-transform transform ${isExpanded ? 'rotate-180 text-pe-accent-text dark:text-[#E6C299]' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                     <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                 </svg>
             </div>
             {isExpanded && <div className="p-4 border-t border-pe-border dark:border-[#1a1a1a] bg-pe-sunken dark:bg-[#050505] shadow-inner">{children}</div>}
         </div>
     );
};

const ExplanationToggle = ({ title, children }: any) => {
     const [isOpen, setIsOpen] = React.useState(false);
     const id = `toggle-${title.replace(/\s+/g, '-')}`;
     return (
         <div className="mb-4 rounded-xl border border-pe-border dark:border-[#222] overflow-hidden shadow-lg">
             <div className="explanation-toggle bg-pe-card dark:bg-[#0a0a0a] hover:bg-pe-hover dark:hover:bg-white/5 transition-colors p-4 cursor-pointer flex items-center gap-3" onClick={() => setIsOpen(!isOpen)}>
                  <input type="checkbox" id={id} checked={isOpen} readOnly className="pointer-events-none accent-[#E6C299] w-4 h-4" />
                  <label htmlFor={id} className="cursor-pointer font-bold text-sm uppercase tracking-widest text-pe-muted dark:text-gray-300 pointer-events-none">{title}</label>
               </div>
               {isOpen && ( <div className="explanation-content bg-pe-sunken dark:bg-[#050505] p-5 text-pe-muted dark:text-gray-400 text-sm leading-relaxed border-t border-pe-border dark:border-[#1a1a1a] shadow-inner">{children}</div> )}
         </div>
     );
};

const SmartBruttoNetto = ({ mwstSatz, currencySymbol }: { mwstSatz: number, currencySymbol: string }) => {
    const [mode, setMode] = React.useState('bn');
    const [inputValue, setInputValue] = React.useState(30);
    const mwstFaktor = 1 + (mwstSatz / 100);
    const result = useMemo(() => mode === 'bn' ? inputValue / mwstFaktor : inputValue * mwstFaktor, [inputValue, mode, mwstFaktor]);
    // MwSt-Anteil: bei Brutto→Netto abgezogen, bei Netto→Brutto aufgeschlagen
    const mwstBetrag = useMemo(() => Math.abs(result - inputValue), [result, inputValue]);
    const mwstLabel = mode === 'bn' ? 'MwSt abgezogen' : 'MwSt enthalten';
    return (
        <div className="flex flex-col items-center w-full carbon-panel shadow-2xl pb-6 bg-pe-card dark:bg-[#030303]">
            <div className="carbon-content w-full">
                <div className="carbon-weave-bg px-6 py-4 border-b border-pe-border dark:border-[#222] mb-6">
                    <h3 className="text-sm font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest">Brutto/Netto ({mwstSatz}%)</h3>
                </div>
                <div className="px-6 flex justify-center mb-6">
                    <div className="toggle-container shadow-lg" onClick={() => setMode(mode === 'bn' ? 'nb' : 'bn')}>
                        <div className={`toggle-pill ${mode === 'bn' ? 'toggle-pill-active' : 'toggle-pill-inactive'}`}>Brutto</div>
                        <div className="toggle-arrow">⇄</div>
                        <div className={`toggle-pill ${mode === 'nb' ? 'toggle-pill-active' : 'toggle-pill-inactive'}`}>Netto</div>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4 items-end px-6">
                    <div><label className="invest-input-label !text-[10px] !text-stone-500 dark:!text-gray-500 !tracking-widest uppercase">Eingabe</label><EditableField value={inputValue} onSave={setInputValue} format="currency" unit={currencySymbol} className="!h-12 !bg-pe-input dark:!bg-[#000] !border-pe-border-strong dark:!border-[#333] shadow-inner" tooltip="Gib hier den Betrag ein, den du umrechnen möchtest." /></div>
                    <div><label className="invest-input-label !text-[10px] !text-stone-500 dark:!text-gray-500 !tracking-widest uppercase">Ergebnis</label><CalculatedField value={result} format="currency" unit={currencySymbol} className="!h-12 !bg-pe-elevated dark:!bg-[#111] !border-pe-border dark:!border-[#222] border shadow-inner" tooltip="Der errechnete Betrag (inkl. oder exkl. MwSt)." /></div>
                </div>
                <div className="mx-6 mt-4 flex justify-between items-baseline border-t border-pe-border dark:border-[#222] pt-3 bg-pe-sunken dark:bg-[#050505] px-3 py-2.5 rounded-lg shadow-inner">
                    <span className="text-[10px] text-stone-500 dark:text-gray-500 uppercase tracking-widest font-bold">
                        {mwstLabel} ({mwstSatz}%)
                    </span>
                    <span className="text-sm font-bold text-pe-accent-text dark:text-[#E6C299] font-mono drop-shadow-md">
                        {formatNumberDisplay(mwstBetrag, 2, 2)}
                        <span className="text-[10px] text-stone-500 dark:text-gray-600 font-sans ml-1">{currencySymbol}</span>
                    </span>
                </div>
            </div>
        </div>
    );
};

const SmartMonatJahr = () => {
    const [mode, setMode] = React.useState('mj');
    const [inputValue, setInputValue] = React.useState(48);
    const handleToggle = () => {
        const nextValue = mode === 'mj' ? Math.round(inputValue / 12) : Math.round(inputValue * 12);
        setInputValue(nextValue);
        setMode(mode === 'mj' ? 'jm' : 'mj');
    };
    const result = useMemo(() => mode === 'mj' ? inputValue / 12 : inputValue * 12, [inputValue, mode]);
    return (
        <div className="flex flex-col items-center w-full carbon-panel shadow-2xl pb-6 bg-pe-card dark:bg-[#030303]">
            <div className="carbon-content w-full">
                <div className="carbon-weave-bg px-6 py-4 border-b border-pe-border dark:border-[#222] mb-6">
                    <h3 className="text-sm font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest">Monat/Jahr Tool</h3>
                </div>
                <div className="px-6 flex justify-center mb-6">
                    <div className="toggle-container shadow-lg" onClick={handleToggle}>
                        <div className={`toggle-pill ${mode === 'mj' ? 'toggle-pill-active' : 'toggle-pill-inactive'}`}>Monate</div>
                        <div className="toggle-arrow">⇄</div>
                        <div className={`toggle-pill ${mode === 'jm' ? 'toggle-pill-active' : 'toggle-pill-inactive'}`}>Jahre</div>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4 items-end px-6">
                    <div><label className="invest-input-label !text-[10px] !text-stone-500 dark:!text-gray-500 !tracking-widest uppercase">Eingabe</label><EditableField value={inputValue} onSave={setInputValue} format={mode === 'mj' ? "integer" : "decimal1"} unit={mode === 'mj' ? 'Mon' : 'Jahr'} className="!h-12 !bg-pe-input dark:!bg-[#000] !border-pe-border-strong dark:!border-[#333] shadow-inner" tooltip="Gib hier die Laufzeit ein, die du umrechnen möchtest." /></div>
                    <div><label className="invest-input-label !text-[10px] !text-stone-500 dark:!text-gray-500 !tracking-widest uppercase">Ergebnis</label><CalculatedField value={result} format={mode === 'mj' ? "decimal1" : "integer"} unit={mode === 'mj' ? 'Jahr' : 'Mon'} className="!h-12 !bg-pe-elevated dark:!bg-[#111] !border-pe-border dark:!border-[#222] border shadow-inner" tooltip="Das errechnete Ergebnis in der jeweils anderen Zeiteinheit." /></div>
                </div>
            </div>
        </div>
    );
};

const InvestRechnerPage: React.FC = () => {
    const { clientData, settings, updateNestedClientData } = useAppStore();
    
    // BYPASS: Wir nutzen 'as any', um TypeScript-Fehler zu unterdrücken
    const safeSettings = settings as any;
    const safeClientData = clientData as any;

    // FIX: Korrekter Pfad laut settingsSlice.ts ist settings.country.currency
    const currencySymbol = currencyLabel(
        safeSettings?.country?.currency,
        safeClientData?.land || safeSettings?.selectedCountryId
    );

    // Dynamische MwSt aus den Store-Ländereinstellungen (inkl. CH 100k MWSTG-Schwelle)
    const mwstSatz = useMemo(() => {
        return resolveEffectiveClientMwstSatz(safeSettings, safeClientData);
    }, [safeClientData, safeSettings]);

    // 💉 CHIRURGIE-FIX: Dynamische Gewinnlinie zieht nun priorisiert aus den Globalen Daten!
    const rentabilitaetZiel = useMemo(() => {
        const globals = safeSettings?.global || {};
        
        // Zieht exakt den Wert 'Gewinnlinie' aus Firebase
        const val = globals.Gewinnlinie ?? 
                    safeClientData?.lohnfaktorData?.gewinnziel ?? 
                    25; 
                    
        return parseNumber(val);
    }, [safeSettings?.global, safeClientData?.lohnfaktorData]);
    
    const investData = safeClientData?.investitionData || {};
    const investition = Number(investData.investition) || 0;
    const laufzeit = Number(investData.laufzeit) || 12;
    const kundenbon = Number(investData.kundenbon) || 900;
    const mode = investData.mode ?? null;
    /** Anzeige/Eingabe der Investition: intern immer netto gespeichert */
    const investEingabeModus: 'brutto' | 'netto' =
        investData.investitionEingabeModus === 'brutto' ? 'brutto' : 'netto';

    const [isGewinnExpanded, setIsGewinnExpanded] = React.useState(true);
    const [isGewinnDetailsExpanded, setIsGewinnDetailsExpanded] = React.useState(false);
    const [isKostenExpanded, setIsKostenExpanded] = React.useState(false);

    // Beim Betreten: Modus neu wählen + immer oben starten (sonst mittig ohne Modus-Auswahl)
    useEffect(() => {
        updateNestedClientData('investitionData', 'mode', null);
        const scrollTop = () => {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            const main = document.getElementById('app-main-scroll');
            if (main) main.scrollTop = 0;
            // Falls ein scrollbarer Vorfahre die Position hält
            let el: HTMLElement | null = document.getElementById('clean-container');
            while (el) {
                if (el.scrollTop > 0) el.scrollTop = 0;
                el = el.parentElement;
            }
        };
        scrollTop();
        // Nach Layout/Zoom nochmal (sonst bleibt Rest-Scroll von der Vorseite)
        requestAnimationFrame(scrollTop);
        const t = window.setTimeout(scrollTop, 50);
        return () => window.clearTimeout(t);
    }, [updateNestedClientData]);

    const mwstFaktor = 1 + (mwstSatz / 100);

    /** Anzeigewert im gewählten Eingabe-Modus (intern investition = netto) */
    const investitionAnzeige = useMemo(
        () => (investEingabeModus === 'brutto' ? investition * mwstFaktor : investition),
        [investition, investEingabeModus, mwstFaktor]
    );

    const monatsbeitrag = useMemo(
        () => (mode === 'monatsbeitrag' ? investition : laufzeit > 0 ? investition / laufzeit : 0),
        [investition, mode, laufzeit]
    );
    
    /**
     * DYNAMISCHE UMSATZ-LOGIK (Netto-Investition → Netto-Zielumsatz → Brutto-Anzeige):
     * (Rate * 5) / (1 - (Rentabilität / 100))
     */
    const umsatzZielNettoMonat = useMemo(() => {
        const basisKostenDeckung = monatsbeitrag * 5; 
        const divisor = 1 - (rentabilitaetZiel / 100);
        return (divisor > 0 && divisor < 1) ? basisKostenDeckung / divisor : basisKostenDeckung * 1.33; 
    }, [monatsbeitrag, rentabilitaetZiel]);

    const umsatzZielBruttoMonat = umsatzZielNettoMonat * mwstFaktor; 
    const umsatzZielBruttoJahr = umsatzZielBruttoMonat * 12;
    // Kundenbon kommt aus dem Kassensystem immer brutto → Brutto / Brutto
    const kundenProMonat = kundenbon > 0 ? (umsatzZielBruttoMonat / kundenbon) : 0;
    const kundenProWoche = kundenProMonat / 4.33;
    const kundenProTag = kundenProWoche / 5;

    const save = (key: string, val: any) => updateNestedClientData('investitionData', key, val);

    const setInvestEingabeModus = (next: 'brutto' | 'netto') => {
        if (next === investEingabeModus) return;
        save('investitionEingabeModus', next);
    };

    // Spezial-Save für Investition: Anzeige je Modus, Speicherung immer netto; Modus-Reset (UI-Lock)
    const handleInvestitionChange = (val: any) => {
        const raw = parseNumber(val);
        const netto = investEingabeModus === 'brutto' && mwstFaktor > 0 ? raw / mwstFaktor : raw;
        updateNestedClientData('investitionData', 'investition', netto);
        updateNestedClientData('investitionData', 'mode', null);
    };

    return (
        <div id="clean-container" className="pb-32 font-sans">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&family=Inter:wght@300;400;600;800&display=swap');

                :root { --custom-beige: #E6C299; --slate-800: #1e293b; --slate-900: #0f172a; --slate-700: #334155; }
                #clean-container { width: 100%; max-width: 1400px; margin: 0 auto; padding: 1rem; color: #f3f4f6; }
                
                .font-sans { font-family: 'Inter', sans-serif; }
                .font-mono { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }

                /* Carbon Weave Design */
                .carbon-weave-bg {
                    background-color: #111;
                    background-image:
                        radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                        radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                        radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                        radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                        radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
                    background-size: 20px 20px;
                }

                .carbon-panel {
                    position: relative;
                    border-radius: 1.5rem;
                    border: 1px solid #1a1a1a;
                    box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.02);
                    overflow: hidden;
                    z-index: 1;
                }

                .carbon-panel::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.55));
                    pointer-events: none;
                    z-index: 1;
                }

                .carbon-content {
                    position: relative;
                    z-index: 10;
                }

                .invest-section { padding: 2rem 0; border-bottom: 1px solid #1a1a1a; }
                .result-kachel-elite { background-color: #050505; border: 1px solid #222; border-radius: 1rem; padding: 1.5rem; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 120px; }
                .result-content { display: flex; align-items: baseline; gap: 0.5rem; margin-top: 0.5rem; }
                .result-content .value { color: var(--custom-beige); font-weight: 900; font-size: 1.8rem; font-family: 'JetBrains Mono', monospace; }
                .result-content .unit { color: #64748b; font-size: 0.75rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; }
                
                .noble-input-wrapper { background: #000; border: 1px solid #333; border-radius: 0.5rem; height: 50px; width: 100%; min-width: 0; max-width: 100%; display: flex; align-items: center; padding: 0 0.5rem; position: relative; box-shadow: inset 0 2px 10px rgba(0,0,0,0.5); transition: border-color 0.2s; box-sizing: border-box;}
                .noble-input-wrapper:focus-within { border-color: #E6C299; box-shadow: inset 0 2px 10px rgba(0,0,0,0.5), 0 0 0 1px #E6C299; }
                .noble-input-wrapper input { background: transparent; border: none; color: #fff; width: 100%; min-width: 0; text-align: center; outline: none; font-family: 'JetBrains Mono', monospace; font-size: 1rem; padding-right: 2.35rem; font-weight: bold; letter-spacing: 0.01em;}
                .noble-suffix { position: absolute; right: 0.45rem; top: 50%; transform: translateY(-50%); color: #64748b; font-weight: 800; font-size: 0.55rem; pointer-events: none; text-transform: uppercase; letter-spacing: 0.06em; white-space: nowrap;}
                
                .noble-result-wrapper { background-color: #050505; border-radius: 0.5rem; height: 50px; display: flex; align-items: center; justify-content: center; position: relative; padding: 0 0.5rem; width: 100%; min-width: 0; border: 1px solid #222; box-sizing: border-box;}
                .noble-result-wrapper input { background: transparent; border: none; color: var(--custom-beige); font-family: 'JetBrains Mono', monospace; font-size: 1.125rem; font-weight: 700; width: 100%; text-align: center; outline: none; padding-right: 1.5rem; pointer-events: none; }
                
                /* Stepper Button Styles — schmaler, damit die Zahl mehr Platz bekommt */
                .stepper-btn { width: 2.1rem; height: 50px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background-color: #111; border: 1px solid #333; border-radius: 0.5rem; color: var(--custom-beige); font-size: 1.25rem; font-weight: bold; cursor: pointer; transition: all 0.2s; box-sizing: border-box; }
                .stepper-btn:hover:not(:disabled) { background-color: #222; border-color: var(--custom-beige); color: white;}
                .stepper-btn:disabled { opacity: 0.5; cursor: not-allowed; }

                .mode-btn { width: 100%; padding: 0.85rem; border-radius: 0.5rem; font-weight: 800; transition: all 0.3s; border: 1px solid transparent; cursor: pointer; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.1em;}
                .mode-btn-active { background-color: var(--custom-beige); color: #000; border-color: var(--custom-beige); box-shadow: 0 0 15px rgba(230,194,153,0.3);}
                .mode-btn-inactive { background-color: #050505; color: #475569; border-color: #222; }
                .mode-btn-inactive:hover { background-color: #111; color: white;}
                
                .toggle-container { background: #000; border: 1px solid #333; border-radius: 99px; display: inline-flex; padding: 4px; cursor: pointer; align-items: center; }
                .toggle-pill { padding: 6px 16px; font-size: 0.7rem; font-weight: 800; border-radius: 99px; text-transform: uppercase; letter-spacing: 0.1em; transition: all 0.2s;}
                .toggle-pill-active { background: var(--custom-beige); color: #000; box-shadow: 0 2px 10px rgba(230,194,153,0.3);}
                .toggle-pill-inactive { color: #64748b; }
                .toggle-pill-inactive:hover { color: white; }
                .toggle-arrow { color: var(--custom-beige); font-size: 0.8rem; padding: 0 8px; opacity: 0.5;}
                
                .invest-input-label { font-size: 0.75rem; font-weight: 800; color: #94a3b8; text-align: center; margin-bottom: 0.5rem; display: block; text-transform: uppercase; letter-spacing: 0.1em;}
                
                /* 💉 CHIRURGIE: Lock-Modus & Aggressives Blinken */
                .ui-lock { opacity: 0.15; pointer-events: none; filter: blur(5px) grayscale(1); transition: all 0.5s ease; }
                .blink-red { animation: subtle-pulse 1.5s infinite; border: 2px solid #ef4444 !important; box-shadow: 0 0 15px rgba(239, 68, 68, 0.4); }
                @keyframes subtle-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); } 50% { box-shadow: 0 0 0 15px rgba(239, 68, 68, 0.4); } }
                
                .cost-tile-clean { background: #050505; border: 1px solid #222; border-radius: 1rem; padding: 1.5rem; display: flex; flex-direction: column; align-items: center; min-height: 100px; }
                .cost-tile-label { font-size: 0.75rem; font-weight: 800; color: #94a3b8; margin-bottom: 1rem; text-transform: uppercase; letter-spacing: 0.1em; text-align: center;}
                .v-row { display: flex; gap: 0.5rem; align-items: baseline; }
                .v-row .val { color: var(--custom-beige); font-weight: 900; font-size: 1.5rem; font-family: 'JetBrains Mono', monospace; }
                .v-row .unt { color: #64748b; font-size: 0.7rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em;}

                /* ============================================================
                   LIGHT-MODE OVERRIDES (Ring 4c) — Dark Mode bleibt pixelidentisch,
                   da alle Regeln nur unter html:not(.dark) greifen.
                   ============================================================ */
                html:not(.dark) #clean-container { color: #1C1917; }
                html:not(.dark) .carbon-weave-bg { background-color: var(--pe-bg-elevated); background-image: none; }
                html:not(.dark) .carbon-panel {
                    border: 1px solid #D6D3D1;
                    box-shadow: 0 10px 30px -12px rgba(28,25,23,0.15);
                }
                html:not(.dark) .carbon-panel::after { background: none; }
                html:not(.dark) .invest-section { border-bottom: 1px solid #D6D3D1; }
                html:not(.dark) .result-kachel-elite { background-color: #FFFFFF; border: 1px solid #D6D3D1; }
                html:not(.dark) .result-content .value { color: #8D6435; }
                html:not(.dark) .result-content .unit { color: #78716C; }
                html:not(.dark) .noble-input-wrapper {
                    background: #FFFFFF;
                    border: 1px solid #B8B2AC;
                    box-shadow: inset 0 1px 3px rgba(28,25,23,0.08);
                }
                html:not(.dark) .noble-input-wrapper:focus-within {
                    border-color: #8D6435;
                    box-shadow: inset 0 1px 3px rgba(28,25,23,0.08), 0 0 0 1px #8D6435;
                }
                html:not(.dark) .noble-input-wrapper input { color: #1C1917; }
                html:not(.dark) .noble-suffix { color: #78716C; }
                html:not(.dark) .noble-result-wrapper { background-color: #F1EFEA; border: 1px solid #D6D3D1; }
                html:not(.dark) .noble-result-wrapper input { color: #8D6435; }
                html:not(.dark) .stepper-btn { background-color: #F1EFEA; border: 1px solid #B8B2AC; color: #8D6435; }
                html:not(.dark) .stepper-btn:hover:not(:disabled) { background-color: #E5E2DD; border-color: #8D6435; color: #1C1917; }
                html:not(.dark) .mode-btn-inactive { background-color: #F1EFEA; color: #78716C; border-color: #D6D3D1; }
                html:not(.dark) .mode-btn-inactive:hover { background-color: #E5E2DD; color: #1C1917; }
                html:not(.dark) .toggle-container { background: #F1EFEA; border: 1px solid #B8B2AC; }
                html:not(.dark) .toggle-pill-inactive { color: #78716C; }
                html:not(.dark) .toggle-pill-inactive:hover { color: #1C1917; }
                html:not(.dark) .invest-input-label { color: #57534E; }
                html:not(.dark) .cost-tile-clean { background: #FFFFFF; border: 1px solid #D6D3D1; }
                html:not(.dark) .cost-tile-label { color: #57534E; }
                html:not(.dark) .v-row .val { color: #8D6435; }
                html:not(.dark) .v-row .unt { color: #78716C; }
                html:not(.dark) .text-customBeige { color: #8D6435; text-shadow: none; }
            `}</style>

            <div className="invest-section pt-8">
                <div className="grid grid-cols-1 md:grid-cols-[minmax(13.5rem,1.45fr)_minmax(9rem,1fr)_minmax(8.5rem,0.95fr)_minmax(11.5rem,1.25fr)] gap-5 md:gap-6 items-start max-w-6xl mx-auto">
                    <div className="text-center rounded-xl border border-pe-border dark:border-[#2a2a2a] p-3 bg-pe-card dark:bg-[#030303] min-w-0">
                        <label className="invest-input-label">
                            Investition ({investEingabeModus === 'brutto' ? 'brutto' : 'netto'})
                        </label>
                        <div className="flex justify-center gap-1 mb-2">
                            <button
                                type="button"
                                onClick={() => setInvestEingabeModus('brutto')}
                                className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider border ${
                                    investEingabeModus === 'brutto'
                                        ? 'bg-[#E6C299] text-black border-[#E6C299]'
                                        : 'border-pe-border-strong text-pe-muted'
                                }`}
                            >
                                Brutto
                            </button>
                            <button
                                type="button"
                                onClick={() => setInvestEingabeModus('netto')}
                                className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-wider border ${
                                    investEingabeModus === 'netto'
                                        ? 'bg-[#E6C299] text-black border-[#E6C299]'
                                        : 'border-pe-border-strong text-pe-muted'
                                }`}
                            >
                                Netto
                            </button>
                        </div>
                        <StepperField
                            value={investitionAnzeige}
                            onSave={handleInvestitionChange}
                            step={100}
                            format="integer"
                            unit={currencySymbol}
                            minDigits={4}
                            tooltip={
                                investEingabeModus === 'brutto'
                                    ? `Brutto-Eingabe — intern wird für die Kalkulation durch ${mwstSatz}% MwSt auf Netto umgerechnet.`
                                    : 'Netto-Gesamtsumme / Netto-Monatsrate der geplanten Investition (ohne MwSt).'
                            }
                        />
                        {investEingabeModus === 'brutto' && mwstSatz > 0 && (
                            <p className="mt-2 text-[10px] text-pe-muted uppercase tracking-wider">
                                = {formatNumberDisplay(investition, 0, 0)} {currencySymbol} netto
                            </p>
                        )}
                    </div>
                    <div className="flex flex-col items-center rounded-xl border border-pe-border dark:border-[#2a2a2a] p-3 bg-pe-card dark:bg-[#030303] min-w-0">
                        <label className="invest-input-label">Modus wählen</label>
                        <div 
                            className={`flex flex-col gap-2 w-full max-w-[200px] p-2 rounded-xl border border-pe-border dark:border-[#222] bg-transparent cursor-help shadow-inner ${!mode ? 'blink-red' : ''}`}
                            data-tooltip-id="app-tooltip" 
                            data-tooltip-content="Wähle, ob die Summe auf einmal (Gesamtbetrag) oder monatlich (z.B. Leasing) gezahlt wird."
                        >
                            <button onClick={() => { save('mode', 'gesamtbetrag'); setIsGewinnDetailsExpanded(false); }} className={`mode-btn ${mode === 'gesamtbetrag' ? 'mode-btn-active' : 'mode-btn-inactive'}`}>Gesamtbetrag</button>
                            <button onClick={() => { save('mode', 'monatsbeitrag'); setIsGewinnDetailsExpanded(true); }} className={`mode-btn ${mode === 'monatsbeitrag' ? 'mode-btn-active' : 'mode-btn-inactive'}`}>Monatsbeitrag</button>
                        </div>
                    </div>
                    <div className="text-center rounded-xl border border-pe-border dark:border-[#2a2a2a] p-3 bg-pe-card dark:bg-[#030303] min-w-0">
                        <label className="invest-input-label">{mode === 'gesamtbetrag' ? 'Amortisation' : 'Laufzeit'} (Mon)</label>
                        <StepperField value={laufzeit} onSave={(v: any) => save('laufzeit', v)} step={12} disabled={!mode} unit="Mon" minDigits={2} tooltip="Die Laufzeit in Monaten, auf die die Investition kalkuliert wird." />
                    </div>
                    <div className="text-center rounded-xl border border-pe-border dark:border-[#2a2a2a] p-3 bg-pe-card dark:bg-[#030303] min-w-0">
                        <label className="invest-input-label">Kundenbon/Rate (Brutto)</label>
                        <StepperField
                            value={kundenbon}
                            onSave={(v: any) => save('kundenbon', v)}
                            step={100}
                            disabled={!mode}
                            unit={currencySymbol}
                            minDigits={3}
                            tooltip="Durchschnittlicher Brutto-Kundenbon bzw. Brutto-Rate aus dem Kassensystem (immer brutto — so wie die Kasse ihn ausweist)."
                        />
                    </div>
                </div>
            </div>

            <div className={!mode ? 'ui-lock' : ''}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mt-12 max-w-7xl mx-auto">
                    <div className="lg:col-span-2">
                        <CollapseSection title={`Anwendungen / Benötigter Umsatz für ${rentabilitaetZiel}% Gewinn`} isExpanded={isGewinnExpanded} setIsExpanded={setIsGewinnExpanded}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ResultBox label="Anw./Kunden (p. Tag)" value={kundenProTag} unit="Kunden" format="decimal" modeActive={!!mode} tooltip="Die Anzahl an zusätzlichen Kunden, die benötigt wird, um dieses Ziel pro Tag zu erreichen." />
                                <ResultBox label="Umsatz (Brutto p. Tag)" value={umsatzZielBruttoMonat / 21.67} unit={currencySymbol} modeActive={!!mode} tooltip="Der zusätzliche Brutto-Umsatz, der benötigt wird, um dieses Ziel pro Tag zu erreichen." />
                                <ResultBox label="Anw./Kunden (p. Woche)" value={kundenProWoche} unit="Kunden" format="decimal" modeActive={!!mode} tooltip="Die Anzahl an zusätzlichen Kunden, die benötigt wird, um dieses Ziel pro Woche zu erreichen." />
                                <ResultBox label="Umsatz (Brutto p. Woche)" value={umsatzZielBruttoMonat / 4.33} unit={currencySymbol} modeActive={!!mode} tooltip="Der zusätzliche Brutto-Umsatz, der benötigt wird, um dieses Ziel pro Woche zu erreichen." />
                                <ResultBox label="Anw./Kunden (p. Monat)" value={kundenProMonat} unit="Kunden" format="decimal" modeActive={!!mode} tooltip="Die Anzahl an zusätzlichen Kunden, die benötigt wird, um dieses Ziel pro Monat zu erreichen." />
                                <ResultBox label="Umsatz (Brutto p. Monat)" value={umsatzZielBruttoMonat} unit={currencySymbol} modeActive={!!mode} tooltip="Der zusätzliche Brutto-Umsatz, der benötigt wird, um dieses Ziel pro Monat zu erreichen." />
                            </div>
                        </CollapseSection>

                        <CollapseSection title="Gewinn-Generierung (Jahr / Gesamtzeitraum)" isExpanded={isGewinnDetailsExpanded} setIsExpanded={setIsGewinnDetailsExpanded}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <ResultBox label="Anw./Kunden (p. Jahr)" value={kundenProMonat * 12} unit="Kunden" format="decimal" modeActive={!!mode} tooltip="Die Anzahl an zusätzlichen Kunden, die benötigt wird, um dieses Ziel pro Jahr zu erreichen." />
                                <ResultBox label="Umsatz (Brutto p. Jahr)" value={umsatzZielBruttoJahr} unit={currencySymbol} modeActive={!!mode} tooltip="Der zusätzliche Brutto-Umsatz, der benötigt wird, um dieses Ziel pro Jahr zu erreichen." />
                                {laufzeit !== 12 && (
                                    <>
                                        <ResultBox label="Anw./Kunden (Gesamt)" value={kundenProMonat * laufzeit} unit="Kunden" format="decimal" modeActive={!!mode} tooltip="Die Anzahl an zusätzlichen Kunden, die benötigt wird, um dieses Ziel über die gesamte Laufzeit zu erreichen." />
                                        <ResultBox label="Umsatz (Brutto Gesamt)" value={umsatzZielBruttoMonat * laufzeit} unit={currencySymbol} modeActive={!!mode} tooltip="Der zusätzliche Brutto-Umsatz, der benötigt wird, um dieses Ziel über die gesamte Laufzeit zu erreichen." />
                                    </>
                                )}
                            </div>
                        </CollapseSection>

                        <CollapseSection title="Umsätze nur für Kostendeckung/Amortisierung (0% Gewinn)" isExpanded={isKostenExpanded} setIsExpanded={setIsKostenExpanded}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <CostTile title="Umsatz p. Monat" netto={monatsbeitrag * 5} brutto={monatsbeitrag * 5 * mwstFaktor} tooltip="Der benötigte Umsatz pro Monat zur reinen Kostendeckung (ohne Gewinnmarge)." />
                                <CostTile title="Umsatz p. Jahr" netto={monatsbeitrag * 5 * 12} brutto={monatsbeitrag * 5 * 12 * mwstFaktor} tooltip="Der benötigte Umsatz pro Jahr zur reinen Kostendeckung (ohne Gewinnmarge)." />
                            </div>
                        </CollapseSection>
                    </div>

                    <div className="lg:col-span-1 flex flex-col gap-10">
                         <div className="carbon-panel p-6 shadow-2xl text-center bg-pe-card dark:bg-[#030303]">
                            <div className="carbon-content">
                                <h4 className="text-pe-muted dark:text-gray-400 font-black mb-6 uppercase tracking-widest text-sm">Investitions-Übersicht</h4>
                                <div className="flex flex-col gap-4">
                                    <div className="flex justify-between items-baseline border-b border-pe-border dark:border-[#222] pb-3 bg-pe-sunken dark:bg-[#050505] p-3 rounded-lg shadow-inner"><span className="text-xs text-stone-500 dark:text-gray-500 uppercase tracking-widest font-bold">Rate (netto)</span><span className="text-xl font-bold text-customBeige font-mono drop-shadow-md">{mode ? formatNumberDisplay(monatsbeitrag) : '---'} <span className="text-xs text-stone-500 dark:text-gray-600 font-sans ml-1">{currencySymbol}</span></span></div>
                                    <div className="flex justify-between items-baseline bg-pe-sunken dark:bg-[#050505] p-3 rounded-lg shadow-inner"><span className="text-xs text-stone-500 dark:text-gray-500 uppercase tracking-widest font-bold">Gesamt (netto)</span><span className="text-xl font-bold text-pe-text dark:text-white font-mono drop-shadow-md">{mode ? formatNumberDisplay(mode === 'monatsbeitrag' ? monatsbeitrag * laufzeit : investition) : '---'} <span className="text-xs text-stone-500 dark:text-gray-600 font-sans ml-1">{currencySymbol}</span></span></div>
                                </div>
                            </div>
                         </div>
                        <SmartBruttoNetto mwstSatz={mwstSatz} currencySymbol={currencySymbol} />
                        <SmartMonatJahr />
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto mt-24 mb-16 carbon-panel shadow-2xl border-none bg-pe-card dark:bg-[#030303]">
                <div className="carbon-content">
                    <h3 className="text-xl font-black p-6 carbon-weave-bg text-customBeige uppercase tracking-widest border-b border-pe-border dark:border-[#222]">Allgemeine Erklärung & Details</h3>
                    <div className="p-6 space-y-4">
                        <ExplanationToggle title="Wie rechnet dieses Tool?">
                            <p>Dieses Tool berechnet die notwendige Umsatzsteigerung für eine wirtschaftlich rentable Investition. Während die <strong className="text-pe-text dark:text-white">Amortisation</strong> (Sektion 4) lediglich den Break-Even-Punkt markiert – also den Umsatz, der zur reinen Kostendeckung benötigt wird (basierend auf dem Faktor 5) –, definiert das <strong className="text-pe-text dark:text-white">Gewinn-Ziel</strong> (Sektion 3) Ihren tatsächlichen unternehmerischen Erfolg. Hier wird der Brutto-Umsatz kalkuliert, der nötig ist, um Ihre Ziel-Rentabilität von aktuell <strong className="text-pe-accent-text dark:text-[#E6C299]">{rentabilitaetZiel}%</strong> sicher zu erreichen.</p>
                            <p className="mt-4 text-[10px] text-stone-500 dark:text-gray-500 uppercase tracking-widest font-bold">Formel: (Monatsrate netto × 5) / (1 - {rentabilitaetZiel}/100) → Brutto-Zielumsatz | Kundenanzahl = Brutto-Zielumsatz / Kundenbon (brutto)</p>
                        </ExplanationToggle>
                        <ExplanationToggle title="Was kann ich damit kalkulieren?">
                            <p>Dieser Rechner ist spezialisiert auf investive Ausgaben mit direktem Leistungs- oder Kundenbezug. Er ist das ideale Werkzeug für die Kalkulation von Leasinggeräten (z. B. Laser, apparative Kosmetik), Seminaren, Schulungen oder gezielten Marketing-Budgets, die über einen definierten Zeitraum (X Monate) einen messbaren Ertrag liefern müssen.</p>
                            <p className="mt-4"><strong className="text-pe-text dark:text-white">Weniger geeignet für:</strong> Gemeinkosten wie Miete oder Strom sowie allgemeine Einrichtung, da diese bereits in der Grundkalkulation des Salons enthalten sind.</p>
                        </ExplanationToggle>
                        <ExplanationToggle title={`Warum ${rentabilitaetZiel}% Rentabilität?`}>
                            <p>Eine Rentabilität von {rentabilitaetZiel} % ist das Fundament für die finanzielle Gesundheit Ihres Unternehmens. Diese Marge sichert nicht nur die Refinanzierung der Investition, sondern ermöglicht den Aufbau von Rücklagen für zukünftige Innovationen.</p>
                            <p className="mt-4">Sie dient als essenzieller Puffer, damit Ihr Salon auch bei unvorhersehbaren Marktschwankungen liquide bleibt und Sie jederzeit handlungsfähig für neue Projekte sind.</p>
                        </ExplanationToggle>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvestRechnerPage;